import React, { useCallback, useEffect } from "react";
import { Grid, FormControl, InputLabel, TextField } from "@mui/material";
import Autocomplete, {
  AutocompleteChangeReason,
} from "@mui/material/Autocomplete";
import { IMaterUser } from "../../models/user";
import {
  APP,
  DType,
  EAutoSearchFieldName,
  ETab,
  FORM_STYLES,
  S_GRID_PROPS,
  TMasterSearchParameter,
} from "../../constants";
import { debounce } from "lodash";
import { getFilterMasterUser } from "../../API/getFilterMasterUser";
import { noOptionsText } from "../../helpers";

export interface UserAutocompleteProps {
  entryUser: any;
  setEntryUser: React.Dispatch<React.SetStateAction<any>>;
  assignUser: any;
  setAssignUser: React.Dispatch<React.SetStateAction<any>>;
  assignBy: any;
  setAssignBy: React.Dispatch<React.SetStateAction<any>>;
  tabPosition: any;
  loading: boolean;
  entryUserObject?: IMaterUser | null;
  assignUserObject?: IMaterUser | null;
  assignByObject?: IMaterUser | null;
  setEntryUserObject: (value: IMaterUser | null) => void;
  setAssignUserObject: (value: IMaterUser | null) => void;
  setAssignByObject: (value: IMaterUser | null) => void;
  setLoading: (value: boolean) => void;
}

const UserAutocomplete: React.FC<UserAutocompleteProps> = ({
  entryUser,
  assignUser,
  assignBy,
  tabPosition,
  loading,
  setEntryUserObject,
  setAssignUserObject,
  setAssignByObject,
  setLoading,
  setEntryUser,
  setAssignUser,
  setAssignBy,
  entryUserObject,
  assignUserObject,
  assignByObject,
}) => {
  const [entryUserList, setEntryUserList] = React.useState<any>([]);
  const [assignByList, setAssignByList] = React.useState<any>([]);
  const [assignUserList, setAssignUserList] = React.useState<any>([]);

  const handleFilterNameOption = (option: string | IMaterUser): string => {
    if (typeof option === "string") return option;
    const { firstName = "", lastName = "", nameFullLc = "" } = option;
    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    return fullName || nameFullLc;
  };

  const onEntryUserChanged = (
    _event: React.SyntheticEvent<Element, Event>,
    value: any,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      setEntryUserList([]);
      setEntryUserObject(null);
    }
    if (!value || value === "undefined") return;
    setEntryUserObject(value);
  };

  const onAssignUserChanged = (_event: React.SyntheticEvent<Element, Event>, value: any, reason?: any) => {
    if (reason === "clear") {
      setAssignUserList([]);
      setAssignUserObject(null);
    }
    if (!value || value === "undefined") return;
    setAssignUserObject(value);
  };

  const onAssignByChanged = (_event: React.SyntheticEvent<Element, Event>, value: any, reason?: any) => {
    if (reason === "clear") {
      setAssignByList([]);
      setAssignByObject(null);
    }
    if (!value || value === "undefined") return;
    setAssignByObject(value);
  };

  const getFiltersUserDataAPI = async (values: any, type: DType) => {
    if (values?.length > 1) {
      let params: TMasterSearchParameter = {
        search: values.toString(),
        page: APP.overage.userAPIpageLimit,
        limit: APP.overage.userAPILimit,
        includeInactiveUsers: true,
      };
      getFilterMasterUser(params)
        .then((users: IMaterUser[]) => {
          setLoading(false);
          switch (type) {
            case DType.entryBy:
              setEntryUserList(users);
              break;
            case DType.assignTo:
              setAssignUserList(users);
              break;
            case DType.assignBy:
              setAssignByList(users);
              break;
            default:
              break;
          }
        })
        .catch((error) => {
          setLoading(false);
        });
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounced = useCallback(
    debounce((value, type) => {
      getFiltersUserDataAPI(value, type);
    }, 1000),
    []
  );

  const onInputChange = (value: any, type: string) => {
    if (value === "undefined") return;

    switch (type) {
      case DType.assignTo:
        setAssignUserList([]);
        setAssignUser(value);
        break;
      case DType.assignBy:
        setAssignByList([]);
        setAssignBy(value);
        break;
      case DType.entryBy:
        setEntryUserList([]);
        setEntryUser(value);
        break;
      default:
        break;
    }
    setLoading(true);
    debounced(value, type);
  };

  useEffect(() => {
    if (entryUserObject?.as400Id) {
      setEntryUser(entryUserObject.as400Id);
    }
    if (assignUserObject?.as400Id) {
      setAssignUser(assignUserObject.as400Id);
    }
    if (assignByObject?.as400Id) {
      setAssignBy(assignByObject.nameFullLc);
    }
  }, [
    entryUserObject,
    assignUserObject,
    assignByObject,
    setEntryUser,
    setAssignUser,
    setAssignBy,
  ]);
  return (
    <>
      <Grid item {...S_GRID_PROPS}>
        <FormControl fullWidth size="small">
          <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="entryUser">
            Entry User
          </InputLabel>
          <Autocomplete
            disablePortal
            noOptionsText={noOptionsText(
              entryUser,
              loading,
              EAutoSearchFieldName.username
            )}
            size="small"
            key="search-entryUser"
            value={entryUser}
            onInputChange={(e, v) => onInputChange(v, DType.entryBy)}
            onChange={onEntryUserChanged}
            forcePopupIcon={false}
            className="autocomplete"
            inputValue={entryUser}
            getOptionLabel={(option) => option !== undefined && option.as400Id}
            filterOptions={(options) => options}
            options={entryUserList}
            renderInput={(params) => (
              <TextField autoComplete="off" {...params} />
            )}
          />
        </FormControl>
      </Grid>
      {tabPosition !== ETab.myAssignments && tabPosition !== ETab.cleared && (
        <Grid item {...S_GRID_PROPS}>
          <FormControl fullWidth size="small">
            <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="assignedTo">
              Assigned To
            </InputLabel>
            <Autocomplete
              disablePortal
              noOptionsText={noOptionsText(
                assignUser,
                loading,
                EAutoSearchFieldName.name
              )}
              size="small"
              key="search-assignedTo"
              value={assignUser}
              onInputChange={(e, v) => onInputChange(v, DType.assignTo)}
              onChange={onAssignUserChanged}
              inputValue={assignUser}
              forcePopupIcon={false}
              className="autocomplete"
              options={assignUserList}
              getOptionLabel={handleFilterNameOption}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField autoComplete="off" {...params} />
              )}
            />
          </FormControl>
        </Grid>
      )}
      {tabPosition === ETab.myAssignments && (
        <Grid item {...S_GRID_PROPS}>
          <FormControl fullWidth size="small">
            <InputLabel style={FORM_STYLES.LABEL} shrink={true} id="assignedTo">
              Assigned By
            </InputLabel>
            <Autocomplete
              disablePortal
              noOptionsText={noOptionsText(
                assignBy,
                loading,
                EAutoSearchFieldName.name
              )}
              size="small"
              key="search-assignedBy"
              value={assignBy}
              onInputChange={(e, v) => onInputChange(v, DType.assignBy)}
              onChange={onAssignByChanged}
              inputValue={assignBy}
              forcePopupIcon={false}
              className="autocomplete"
              options={assignByList}
              getOptionLabel={handleFilterNameOption}
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField autoComplete="off" {...params} />
              )}
            />
          </FormControl>
        </Grid>
      )}
    </>
  );
};

export default UserAutocomplete;
